
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  validateDebugMode,
  getDebugParams,
  getDebugDate,
  setDebugDate as setDebugDateUtil,
  isDebugDateMode
} from '@/lib/debugUtils';
import { normalizeDate } from '@/lib/dateUtils';

interface DebugDateContextType {
  debugDate: Date | null;
  setDebugDate: (date: Date | null) => void;
  isDebugMode: boolean;
}

const DebugDateContext = createContext<DebugDateContextType>({
  debugDate: null,
  setDebugDate: () => {},
  isDebugMode: false
});

export const useDebugDate = () => useContext(DebugDateContext);

export const DebugDateProvider = ({ children }: { children: React.ReactNode }) => {
  // Initialize state based on URL parameters and stored debug date
  const [debugDate, setDebugDateState] = useState<Date | null>(() => {
    if (isDebugDateMode()) {
      console.log('🔄 Debug mode initialization:', {
        status: 'active',
        hasDebugParam: true,
        timestamp: new Date().toISOString()
      });
      
      // Get debug date from storage using the safe utility function
      const savedDate = getDebugDate();
      
      if (savedDate) {
        // Normalize the date to midnight
        const normalizedDate = normalizeDate(savedDate);
        console.log('✅ Debug mode initialized with normalized saved date:', normalizedDate.toISOString());
        return normalizedDate;
      }
      
      // If no saved date, use current date and save it
      const currentDate = normalizeDate(new Date());
      setDebugDateUtil(currentDate);
      console.log('✅ Debug mode initialized with current date:', currentDate.toISOString());
      return currentDate;
    }
    
    console.log('ℹ️ Debug mode status: inactive');
    return null;
  });

  const isDebugMode = useMemo(() => {
    return isDebugDateMode() && debugDate !== null;
  }, [debugDate]);

  // Memoize the setDebugDate function to avoid unnecessary re-renders
  const setDebugDate = useCallback((date: Date | null) => {
    if (date && isNaN(date.getTime())) {
      console.error('Invalid date provided to setDebugDate');
      toast.error('Invalid debug date');
      return;
    }
    
    console.log('Setting debug date in context:', {
      date: date ? date.toISOString() : null,
      timestamp: new Date().toISOString()
    });
    
    // Always normalize dates to midnight to ensure consistency
    setDebugDateState(date ? normalizeDate(date) : null);
  }, []);

  // Effect to handle debug mode initialization and validation
  useEffect(() => {
    // Run debug mode validation on mount
    if (isDebugDateMode()) {
      const validation = validateDebugMode();
      console.log('🔍 Debug mode validation on mount:', validation);
      
      if (!debugDate) {
        // Get debug date from storage using the safe utility
        const savedDate = getDebugDate();
        const initialDate = savedDate ? savedDate : new Date();
        const normalized = normalizeDate(initialDate);
        setDebugDateState(normalized);
        
        // Ensure the date is saved to storage
        setDebugDateUtil(normalized);
        
        console.log('Debug date set from URL param:', {
          date: normalized.toISOString(),
          hadSavedDate: !!savedDate,
          timestamp: new Date().toISOString()
        });
        
        if (!validation.datesMatch && validation.hasGameDate) {
          console.warn('⚠️ Debug date and game date mismatch detected');
          toast.warning('Debug mode: Date synchronization issue detected');
        }
      }
    } else if (debugDate) {
      console.log('✅ Exiting debug mode');
      setDebugDateState(null);
      localStorage.removeItem('debug_date');
      
    }
  }, [debugDate]);

  // Persist debug date changes to localStorage
  useEffect(() => {
    if (debugDate) {
      try {
        // Ensure the date is valid
        if (isNaN(debugDate.getTime())) {
          throw new Error('Invalid date object');
        }
        
        // Normalize the date to midnight before saving
        const normalizedDate = normalizeDate(debugDate);
        
        // Validate debug date whenever it changes
        const validation = validateDebugMode();
        console.log('🔄 Debug state updated:', {
          date: normalizedDate.toISOString(),
          formattedDate: normalizedDate.toLocaleDateString(),
          validation,
          timestamp: new Date().toISOString()
        });
        
        // Use the safer utility to save debug date
        setDebugDateUtil(normalizedDate);
      } catch (error) {
        console.error('❌ Invalid debug date detected:', error);
        toast.error('Debug mode: Invalid date detected');
        // Reset to current date if invalid
        setDebugDateState(normalizeDate(new Date()));
      }
    }
  }, [debugDate]);

  const contextValue = useMemo(() => ({
    debugDate,
    setDebugDate,
    isDebugMode
  }), [debugDate, setDebugDate, isDebugMode]);

  return (
    <DebugDateContext.Provider value={contextValue}>
      {children}
    </DebugDateContext.Provider>
  );
};
